import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import {
  exitPosBrowserFullscreen,
  readPosImmersivePreference,
  requestPosBrowserFullscreen,
  syncPosImmersiveDomFlag,
  writePosImmersivePreference,
} from '../utils/posImmersiveMode';

const PosImmersiveContext = createContext(null);

export function PosImmersiveProvider({ children }) {
  const location = useLocation();
  const [active, setActive] = useState(() => readPosImmersivePreference());
  const [fullscreenNative, setFullscreenNative] = useState(false);
  const onCaisseRoute = location.pathname.startsWith('/caisse');

  useEffect(() => {
    syncPosImmersiveDomFlag(active && onCaisseRoute);
    writePosImmersivePreference(active);
  }, [active, onCaisseRoute]);

  useEffect(() => {
    const onChange = () => {
      const native = Boolean(
        document.fullscreenElement || document.webkitFullscreenElement
      );
      setFullscreenNative(native);
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  const enter = useCallback(async () => {
    setActive(true);
    const fs = await requestPosBrowserFullscreen();
    setFullscreenNative(Boolean(fs.ok));
  }, []);

  const exit = useCallback(async () => {
    setActive(false);
    await exitPosBrowserFullscreen();
    setFullscreenNative(false);
  }, []);

  const toggle = useCallback(async () => {
    if (active) await exit();
    else await enter();
  }, [active, enter, exit]);

  const value = useMemo(
    () => ({
      active,
      fullscreenNative,
      enter,
      exit,
      toggle,
    }),
    [active, fullscreenNative, enter, exit, toggle]
  );

  return (
    <PosImmersiveContext.Provider value={value}>{children}</PosImmersiveContext.Provider>
  );
}

export function usePosImmersive() {
  const ctx = useContext(PosImmersiveContext);
  if (!ctx) {
    return {
      active: false,
      fullscreenNative: false,
      enter: async () => {},
      exit: async () => {},
      toggle: async () => {},
    };
  }
  return ctx;
}
